import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Building2, 
  Users, 
  FolderKanban, 
  Download, 
  Settings, 
  LogOut,
  Menu,
  X,
  Shield,
  UserPlus,
  KeyRound
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, hideForCoordinatoreGTL: true },
  { path: '/deleghe', label: 'Delegati C3i', icon: Users, hideForAdminOrdine: true, hideForCoordinatoreGTL: true },
  { path: '/gtl', label: 'GTL', icon: FolderKanban, hideForAdminOrdine: true, hideForCoordinatoreGTL: true },
  { path: '/nomina-delegati', label: 'Nomina delegati', icon: UserPlus, adminOrdineOnly: true },
  { path: '/segnalazioni-gtl', label: 'Segnalazioni per GTL', icon: Users, adminOrdineOnly: true },
  { path: '/impostazioni', label: 'Gestione', icon: Settings, segretarioOnly: true, hideForCoordinatoreGTL: true },
  { path: '/export', label: 'Export', icon: Download, segretarioOnly: true },
];

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isSegretario, isAdminOrdine, isCoordinatoreGTL } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredNavItems = navItems.filter(item => {
    if (item.segretarioOnly && !isSegretario()) return false;
    if (item.adminOrSegretario && !(isSegretario() || isAdminOrdine())) return false;
    if (item.adminOrdineOnly && !isAdminOrdine()) return false;
    if (item.hideForAdminOrdine && isAdminOrdine()) return false;
    if (item.hideForCoordinatoreGTL && isCoordinatoreGTL()) return false;
    return true;
  });

  const getRoleBadge = () => {
    switch(user?.ruolo_con_accesso) {
      case 'AMMINISTRATORE':
        return <span className="badge-success">Amministratore</span>;
      case 'ADMIN_ORDINE':
        return <span className="badge-warning">Admin Ordine</span>;
      case 'CONSIGLIERE_C3I':
        return <span className="badge-neutral">Consigliere C3i</span>;
      case 'COORDINATORE_GTL':
        return <span className="bg-blue-100 text-blue-700 border border-blue-200 inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold">Coordinatore GTL</span>;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <button 
        className="mobile-menu-btn fixed top-4 left-4 z-40"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        data-testid="mobile-menu-btn"
      >
        {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`sidebar ${sidebarOpen ? 'open' : ''} lg:translate-x-0`}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center gap-3">
              <img 
                src="/logo-c3i.png" 
                alt="C3i Logo" 
                className="w-10 h-10 object-contain"
              />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 overflow-y-auto" data-testid="sidebar-nav">
            <ul className="space-y-1">
              {filteredNavItems.map((item) => {
                return (
                  <li key={item.path}>
                    <NavLink
                      to={item.path}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) => 
                        `sidebar-nav-item ${isActive ? 'active' : ''}`
                      }
                      data-testid={`nav-${item.path.replace('/', '')}`}
                    >
                      <item.icon size={20} />
                      <span>{item.label}</span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* User info */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center">
                <span className="text-sm font-medium text-slate-600">
                  {user?.nome?.[0]}{user?.cognome?.[0]}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">
                  {user?.nome} {user?.cognome}
                </p>
                <div className="mt-1">
                  {getRoleBadge()}
                </div>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              onClick={() => { setSidebarOpen(false); navigate('/cambia-password'); }}
              data-testid="change-password-btn"
            >
              <KeyRound size={18} className="mr-2" />
              Cambia password
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut size={18} className="mr-2" />
              Esci
            </Button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <main className="main-content">
        <Outlet />
      </main>
    </div>
  );
}
